/**
 * Ubica sola la cara en la foto (pupilas, línea media, boca) con el reconocimiento de caras de MediaPipe (Google, Apache-2.0).
 * Corre dentro del navegador, en una página interna de Donna (`/cara/detector.html`, con su propia regla de seguridad) que se abre
 * en un marco invisible; los archivos (~16 MB) los sirve Donna misma y se bajan solo la primera vez. La foto no sale a ningún servicio.
 */
import { puntosDesdeCara, clasificarRostro, recorteBoca, type MarcasCara, type ResultadoCara, type ClasificacionRostro, type Caja } from "../../../dominio/src/index.ts";

type Marca = { x: number; y: number };
const CLAVES = ["irisDer", "irisIzq", "nasion", "menton", "comisuraDer", "comisuraIzq", "labioSup", "labioInf", "nariz", "frenteArriba", "pomuloDer", "pomuloIzq", "frenteDer", "frenteIzq", "mandibulaDer", "mandibulaIzq"] as const;

export interface CaraUbicada extends ResultadoCara { rostro: ClasificacionRostro | null; /** recorte de la parte baja de la cara (nariz al mentón) en píxeles de la imagen */ recorte: Caja; /** borde interno de los labios (la abertura de la boca), en píxeles de la imagen; solo cambia lo que queda dentro */ abertura: Marca[] | null }

let marco: Promise<HTMLIFrameElement> | null = null;
let numero = 0;

function abrirMarco(): Promise<HTMLIFrameElement> {
  if (marco) return marco;
  marco = new Promise<HTMLIFrameElement>((ok, mal) => {
    const f = document.createElement("iframe");
    f.src = "/cara/detector.html"; f.hidden = true; f.title = "Reconocimiento de la cara";
    const t = setTimeout(() => { removeEventListener("message", al); f.remove(); mal(new Error("El reconocimiento de la cara no respondió.")); }, 20000);
    const al = (e: MessageEvent) => {
      if (e.origin !== location.origin || e.source !== f.contentWindow || e.data?.tipo !== "listo") return;
      clearTimeout(t); removeEventListener("message", al); ok(f);
    };
    addEventListener("message", al);
    document.body.appendChild(f);
  }).catch((e) => { marco = null; throw e; });
  return marco;
}

let cola: Promise<unknown> = Promise.resolve();

/** Busca la cara en la foto. Devuelve los puntos de la sonrisa ubicados, o null si no encontró una cara. Las búsquedas van de una en una. */
export function ubicarCara(foto: HTMLImageElement | HTMLCanvasElement, ancho: number, alto: number): Promise<CaraUbicada | null> {
  const p = cola.then(() => buscarUna(foto, ancho, alto));
  cola = p.catch(() => undefined);
  return p;
}

async function buscarUna(foto: HTMLImageElement | HTMLCanvasElement, ancho: number, alto: number): Promise<CaraUbicada | null> {
  const f = await abrirMarco();
  const imagen = await createImageBitmap(foto, { resizeWidth: ancho, resizeHeight: alto });
  const id = ++numero;
  const r = await new Promise<{ marcas: Record<string, Marca> | null; error?: string }>((ok, mal) => {
    const t = setTimeout(() => { removeEventListener("message", al); mal(new Error("El reconocimiento de la cara tardó demasiado.")); }, 60000);
    const al = (e: MessageEvent) => {
      if (e.origin !== location.origin || e.source !== f.contentWindow || e.data?.tipo !== "resultado" || e.data.id !== id) return;
      clearTimeout(t); removeEventListener("message", al); ok(e.data);
    };
    addEventListener("message", al);
    f.contentWindow!.postMessage({ tipo: "buscar", id, imagen }, location.origin, [imagen]);
  });
  if (r.error) throw new Error("No se pudo ubicar la cara.");
  if (!r.marcas || CLAVES.some((k) => !r.marcas![k])) return null;
  const P = (k: (typeof CLAVES)[number]): Marca => ({ x: r.marcas![k].x * ancho, y: r.marcas![k].y * alto });
  const marcas: MarcasCara = { irisDer: P("irisDer"), irisIzq: P("irisIzq"), nasion: P("nasion"), menton: P("menton"), comisuraDer: P("comisuraDer"), comisuraIzq: P("comisuraIzq"), labioSup: P("labioSup"), labioInf: P("labioInf") };
  const d = (a: Marca, b: Marca) => Math.hypot(a.x - b.x, a.y - b.y);
  const rostro = clasificarRostro({
    altura: d(P("frenteArriba"), P("menton")), pomulos: d(P("pomuloDer"), P("pomuloIzq")),
    frente: d(P("frenteDer"), P("frenteIzq")), mandibula: d(P("mandibulaDer"), P("mandibulaIzq")),
  });
  const recorte = recorteBoca({ comisuraDer: marcas.comisuraDer, comisuraIzq: marcas.comisuraIzq, nariz: P("nariz"), menton: marcas.menton }, ancho, alto);
  const ab = (r.marcas as unknown as { abertura?: Marca[] }).abertura;
  const abertura = Array.isArray(ab) && ab.length >= 12 ? ab.map((p) => ({ x: p.x * ancho, y: p.y * alto })) : null;
  return { ...puntosDesdeCara(marcas), rostro, recorte, abertura };
}
